import { ArrowClockwise, Check, Copy, FloppyDisk, WarningCircle } from "@phosphor-icons/react";
import { useQuery } from "@tanstack/react-query";
import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ApiClientError, api } from "@/lib/api";
import { calculate, scenarioPrices } from "@/lib/calculator";
import type { HistoryRange, PlanTab } from "@/lib/navigation";
import type { Plan, SaveState } from "@/lib/types";

const PriceHistory = lazy(() => import("./PriceHistory").then((module) => ({ default: module.PriceHistory })));

type PlanDraft = Pick<Plan,
  "name" | "currentShares" | "currentTotalCost" | "currentPriceMode" | "manualCurrentPrice" |
  "fxRateMode" | "manualFxRate" | "addCash" | "buyPrice" | "buyFee" | "targetAverage" |
  "targetBuyPrice" | "maxLossPercent" | "targetSellPrice" | "sellPercent"
>;

const editableKeys: Array<keyof PlanDraft> = [
  "name", "currentShares", "currentTotalCost", "currentPriceMode", "manualCurrentPrice", "fxRateMode", "manualFxRate",
  "addCash", "buyPrice", "buyFee", "targetAverage", "targetBuyPrice", "maxLossPercent", "targetSellPrice", "sellPercent",
];

const toDraft = (plan: Plan): PlanDraft => Object.fromEntries(editableKeys.map((key) => [key, plan[key]])) as PlanDraft;
const fingerprint = (draft: PlanDraft) => JSON.stringify(draft);
const usd = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 });
const thb = new Intl.NumberFormat("th-TH", { style: "currency", currency: "THB", maximumFractionDigits: 0 });
const number = new Intl.NumberFormat("en-US", { maximumFractionDigits: 7 });
const percent = new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 });

function NumericField({ label, value, onChange, hint, disabled }: { label: string; value: string | null; onChange: (value: string) => void; hint?: string; disabled?: boolean }) {
  return (
    <label className="field">
      <span>{label}</span>
      <input inputMode="decimal" value={value ?? ""} disabled={disabled} onChange={(event) => onChange(event.target.value)} />
      {hint ? <small>{hint}</small> : null}
    </label>
  );
}

function Metric({ label, value, tone = "neutral" }: { label: string; value: string; tone?: "neutral" | "positive" | "negative" | "accent" }) {
  return <div className={`metric ${tone}`}><span>{label}</span><strong>{value}</strong></div>;
}

function SaveIndicator({ state }: { state: SaveState }) {
  const content = {
    idle: [<Check key="i" />, "พร้อมบันทึก"],
    saving: [<FloppyDisk className="pulse" key="s" />, "Saving…"],
    saved: [<Check key="d" />, "Saved"],
    error: [<WarningCircle key="e" />, "Save error"],
    conflict: [<WarningCircle key="c" />, "Conflict"],
  }[state];
  return <span className={`save-state ${state}`}>{content[0]}{content[1]}</span>;
}

export function PlanWorkspace({ plan, tab, historyRange, onTabChange, onHistoryRangeChange, onSaved, onReload, onDuplicate }: {
  plan: Plan;
  tab: PlanTab;
  historyRange: HistoryRange;
  onTabChange: (tab: PlanTab) => void;
  onHistoryRangeChange: (range: HistoryRange) => void;
  onSaved: (plan: Plan) => void;
  onReload: () => void;
  onDuplicate: (plan: Plan) => void;
}) {
  const [draft, setDraft] = useState<PlanDraft>(() => toDraft(plan));
  const [saveState, setSaveState] = useState<SaveState>("idle");
  const [saveMessage, setSaveMessage] = useState("");
  const versionRef = useRef(plan.version);
  const savedRef = useRef(fingerprint(toDraft(plan)));
  const activePlanRef = useRef(plan.id);

  const latestPrices = useQuery({
    queryKey: ["latest-price", plan.instrumentId],
    queryFn: () => api.prices(plan.instrumentId, "1M"),
  });
  const fx = useQuery({ queryKey: ["fx-usd-thb"], queryFn: api.fx });
  const latestFinal = latestPrices.data?.prices.findLast((price) => price.status === "FINAL");
  const latestClose = latestFinal?.close ?? "0";
  const latestFx = fx.data?.rate?.close ?? "0";

  useEffect(() => {
    const next = toDraft(plan);
    setDraft(next);
    versionRef.current = plan.version;
    savedRef.current = fingerprint(next);
    activePlanRef.current = plan.id;
    setSaveState("idle");
    setSaveMessage("");
  }, [plan.id]);

  useEffect(() => {
    if (activePlanRef.current !== plan.id || fingerprint(draft) === savedRef.current || saveState === "conflict") return;
    setSaveState("saving");
    const timeout = window.setTimeout(async () => {
      try {
        const response = await api.updatePlan(plan.id, { ...draft, version: versionRef.current });
        if (activePlanRef.current !== plan.id) return;
        versionRef.current = response.plan.version;
        savedRef.current = fingerprint(toDraft(response.plan));
        setSaveState("saved");
        setSaveMessage("");
        onSaved(response.plan);
      } catch (error) {
        if (error instanceof ApiClientError && error.status === 409) {
          setSaveState("conflict");
          setSaveMessage("แผนนี้ถูกแก้จากอีกแท็บ เลือกโหลดข้อมูลล่าสุดหรือทำสำเนาเพื่อเก็บค่าชุดนี้");
        } else {
          setSaveState("error");
          setSaveMessage("บันทึกไม่สำเร็จ ระบบจะลองใหม่เมื่อคุณแก้ข้อมูลอีกครั้ง");
        }
      }
    }, 750);
    return () => window.clearTimeout(timeout);
  }, [draft, onSaved, plan.id, saveState]);

  const set = useCallback(<K extends keyof PlanDraft>(key: K, value: PlanDraft[K]) => {
    setDraft((current) => ({ ...current, [key]: value }));
    if (saveState === "error") setSaveState("idle");
  }, [saveState]);

  const currentPrice = draft.currentPriceMode === "LATEST" ? latestClose : (draft.manualCurrentPrice ?? "0");
  const fxRate = draft.fxRateMode === "LATEST" ? latestFx : (draft.manualFxRate ?? "0");
  const result = useMemo(() => calculate({
    ...draft,
    currentPrice,
    fxRate,
  }), [currentPrice, draft, fxRate]);
  const scenarios = useMemo(() => scenarioPrices(result.price, result.newAverage, Number(draft.targetSellPrice), result.stopPrice), [draft.targetSellPrice, result.newAverage, result.price, result.stopPrice]);
  const useBuyPrice = useCallback((price: string) => {
    setDraft((current) => ({ ...current, buyPrice: price }));
    onTabChange("planner");
  }, [onTabChange]);

  if (tab === "history") {
    return (
      <main className="workspace history-workspace">
        <div className="workspace-topbar">
          <div className="tab-list"><button onClick={() => onTabChange("planner")}>Planner</button><button className="active">Price History</button></div>
          <SaveIndicator state={saveState} />
        </div>
        <Suspense fallback={<div className="empty-state">กำลังเปิดกราฟ…</div>}><PriceHistory instrument={plan.instrument} range={historyRange} onRangeChange={onHistoryRangeChange} onUseBuyPrice={useBuyPrice} /></Suspense>
      </main>
    );
  }

  return (
    <main className="workspace">
      <div className="workspace-topbar">
        <div className="tab-list"><button className="active">Planner</button><button onClick={() => onTabChange("history")}>Price History</button></div>
        <SaveIndicator state={saveState} />
      </div>
      {saveMessage ? (
        <div className={`message-banner ${saveState}`}><span>{saveMessage}</span>{saveState === "conflict" ? <span className="banner-actions"><button onClick={onReload}><ArrowClockwise /> Reload</button><button onClick={() => api.duplicatePlan(plan.id, `${draft.name} conflict copy`).then(({ plan: copy }) => onDuplicate(copy))}><Copy /> Duplicate</button></span> : null}</div>
      ) : null}

      <section className="plan-title-row">
        <div><span className="ticker-pill">{plan.instrument.symbol}</span><input className="plan-name-input" value={draft.name} onChange={(event) => set("name", event.target.value)} aria-label="ชื่อแผน" /></div>
        <p>อัปเดต {new Date(plan.updatedAt).toLocaleString("th-TH", { dateStyle: "medium", timeStyle: "short" })}</p>
      </section>

      <div className="planner-grid">
        <section className="panel input-panel">
          <div className="section-heading"><div><span className="step">01</span><h2>สถานะพอร์ตปัจจุบัน</h2></div><p>Position snapshot</p></div>
          <div className="field-grid">
            <NumericField label="จำนวนหุ้นเดิม" value={draft.currentShares} onChange={(value) => set("currentShares", value)} />
            <NumericField label="ต้นทุนรวมเดิม (USD)" value={draft.currentTotalCost} onChange={(value) => set("currentTotalCost", value)} />
          </div>
          <div className="mode-card">
            <div className="mode-head"><span>Current price</span><div className="mini-segmented"><button className={draft.currentPriceMode === "LATEST" ? "active" : ""} onClick={() => set("currentPriceMode", "LATEST")}>Latest EOD</button><button className={draft.currentPriceMode === "MANUAL" ? "active" : ""} onClick={() => set("currentPriceMode", "MANUAL")}>Manual</button></div></div>
            <NumericField label={draft.currentPriceMode === "LATEST" ? `ราคาล่าสุด · ${latestFinal?.tradeDate ?? "รอข้อมูล"}` : "Scenario price (USD)"} value={currentPrice} onChange={(value) => set("manualCurrentPrice", value)} disabled={draft.currentPriceMode === "LATEST"} />
          </div>
          <div className="mode-card">
            <div className="mode-head"><span>USD / THB</span><div className="mini-segmented"><button className={draft.fxRateMode === "LATEST" ? "active" : ""} onClick={() => set("fxRateMode", "LATEST")}>Latest</button><button className={draft.fxRateMode === "MANUAL" ? "active" : ""} onClick={() => set("fxRateMode", "MANUAL")}>Override</button></div></div>
            <NumericField label={draft.fxRateMode === "LATEST" ? `Daily rate · ${fx.data?.rate?.tradeDate ?? "รอข้อมูล"}` : "กำหนดอัตราเอง"} value={fxRate} onChange={(value) => set("manualFxRate", value)} disabled={draft.fxRateMode === "LATEST"} />
          </div>
          <div className="metric-row"><Metric label="Average เดิม" value={`${number.format(result.oldAverage)} USD`} /><Metric label="Unrealized P/L" value={usd.format(result.oldPnl)} tone={result.oldPnl >= 0 ? "positive" : "negative"} /></div>
        </section>

        <section className="panel input-panel">
          <div className="section-heading"><div><span className="step">02</span><h2>แผนซื้อเพิ่ม</h2></div><p>Average down</p></div>
          <div className="field-grid three"><NumericField label="เงินลงทุนเพิ่ม (USD)" value={draft.addCash} onChange={(value) => set("addCash", value)} /><NumericField label="Buy price (USD)" value={draft.buyPrice} onChange={(value) => set("buyPrice", value)} hint="เลือก Close จาก Price History ได้" /><NumericField label="ค่าธรรมเนียม (USD)" value={draft.buyFee} onChange={(value) => set("buyFee", value)} /></div>
          <div className="result-card accent-card"><div><span>New average</span><strong>{number.format(result.newAverage)} <small>USD/หุ้น</small></strong></div><div className="comparison"><span>{number.format(result.oldAverage)}</span><i>→</i><span>{number.format(result.newAverage)}</span></div></div>
          <div className="metric-row four"><Metric label="หุ้นที่เพิ่ม" value={number.format(result.addedShares)} /><Metric label="หุ้นรวมใหม่" value={number.format(result.newShares)} /><Metric label="ต้นทุนรวมใหม่" value={usd.format(result.newCost)} /><Metric label="P/L ใหม่" value={`${usd.format(result.newPnl)} · ${percent.format(result.newPnlPercent)}%`} tone={result.newPnl >= 0 ? "positive" : "negative"} /></div>
        </section>

        <section className="panel input-panel">
          <div className="section-heading"><div><span className="step">03</span><h2>เป้าหมายต้นทุน</h2></div><p>Target average</p></div>
          <div className="field-grid"><NumericField label="Average เป้าหมาย" value={draft.targetAverage} onChange={(value) => set("targetAverage", value)} /><NumericField label="ราคาที่จะซื้อเพิ่ม" value={draft.targetBuyPrice} onChange={(value) => set("targetBuyPrice", value)} /></div>
          <div className={`result-card ${result.targetReachable ? "" : "warning"}`}><span>เงินที่ต้องใช้โดยประมาณ</span><strong>{result.targetReachable ? usd.format(result.requiredCash) : "ทำไม่ได้ด้วยราคานี้"}</strong><small>{result.targetReachable ? `ซื้อเพิ่มประมาณ ${number.format(result.requiredShares)} หุ้น` : "Buy price ต้องต่ำกว่า Average เป้าหมาย"}</small></div>
        </section>

        <section className="panel input-panel">
          <div className="section-heading"><div><span className="step">04</span><h2>Risk & Sell scenario</h2></div><p>Exit planning</p></div>
          <div className="field-grid three"><NumericField label="ขาดทุนสูงสุด (%)" value={draft.maxLossPercent} hint="สูงสุด 90% ตามสูตรเดิม" onChange={(value) => set("maxLossPercent", value)} /><NumericField label="ราคาเป้าขาย" value={draft.targetSellPrice} onChange={(value) => set("targetSellPrice", value)} /><NumericField label="สัดส่วนที่จะขาย (%)" value={draft.sellPercent} onChange={(value) => set("sellPercent", value)} /></div>
          <div className="metric-row four"><Metric label="Stop price" value={`${number.format(result.stopPrice)} USD`} tone="negative" /><Metric label="หุ้นที่จะขาย" value={number.format(result.sharesToSell)} /><Metric label="เงินรับเป้าหมาย" value={usd.format(result.targetCash)} /><Metric label="P/L เมื่อขาย" value={usd.format(result.targetSellPnl)} tone={result.targetSellPnl >= 0 ? "positive" : "negative"} /></div>
        </section>
      </div>

      <section className="panel scenario-panel">
        <div className="section-heading"><div><span className="eyebrow">Scenario map</span><h2>ถ้าราคาเปลี่ยน พอร์ตจะเป็นอย่างไร</h2></div><p>คำนวณด้วยหุ้นและต้นทุนหลังซื้อเพิ่ม</p></div>
        <div className="table-wrap"><table><thead><tr><th>ราคา</th><th>มูลค่าพอร์ต</th><th>P/L (USD)</th><th>P/L</th><th>P/L (THB)</th><th>สถานะ</th></tr></thead><tbody>{scenarios.map((scenario) => {
          const value = result.newShares * scenario;
          const pnl = value - result.newCost;
          const pnlPercent = result.newCost ? pnl / result.newCost * 100 : 0;
          const label = Math.abs(scenario - result.newAverage) <= 0.01 ? "เท่าทุน" : pnl > 0 ? "กำไร" : scenario > result.price ? "ลดขาดทุน" : "ขาดทุน";
          return <tr key={scenario}><td>{number.format(scenario)}</td><td>{usd.format(value)}</td><td className={pnl >= 0 ? "positive" : "negative"}>{usd.format(pnl)}</td><td className={pnl >= 0 ? "positive" : "negative"}>{percent.format(pnlPercent)}%</td><td>{thb.format(pnl * result.fxRate)}</td><td><span className={`status-pill ${pnl >= 0 ? "good" : "bad"}`}>{label}</span></td></tr>;
        })}</tbody></table></div>
      </section>
    </main>
  );
}
